import { useEffect, useRef, useCallback } from 'react'
import { create } from 'zustand'
import { useMusicStore } from './MusicPlayer'

// ═══ Room Tone Store ═══
interface RoomToneState {
  active: boolean
  toggle: () => void
}

export const useRoomTone = create<RoomToneState>((set) => ({
  active: false,
  toggle: () => set(s => ({ active: !s.active })),
}))

// ═══ Room Tone Engine — Web Audio oscillator ═══
// 160Hz fundamental + 240Hz fifth harmonic, LFO-modulated
// Spec: Timbre (persona) — warm vocal booth ambience

export default function RoomTone() {
  const { active } = useRoomTone()
  const ctxRef = useRef<AudioContext | null>(null)
  const gain1Ref = useRef<GainNode | null>(null)
  const gain2Ref = useRef<GainNode | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rafRef = useRef<number>(0)
  const initializedRef = useRef(false)

  const initAudio = useCallback(() => {
    if (initializedRef.current) return
    initializedRef.current = true

    const ctx = new AudioContext()
    ctxRef.current = ctx

    const analyser = ctx.createAnalyser()
    analyser.fftSize = 256
    analyser.connect(ctx.destination)
    analyserRef.current = analyser

    // Fundamental — 160Hz (vocal booth warmth)
    const osc1 = ctx.createOscillator()
    const g1 = ctx.createGain()
    osc1.type = 'sine'
    osc1.frequency.value = 160
    g1.gain.value = 0
    const lfo1 = ctx.createOscillator()
    const lfo1g = ctx.createGain()
    lfo1.type = 'sine'
    lfo1.frequency.value = 0.08 // ~12 second cycle
    lfo1g.gain.value = 3 // +/- 3Hz drift
    lfo1.connect(lfo1g)
    lfo1g.connect(osc1.frequency)
    lfo1.start()
    osc1.connect(g1)
    g1.connect(analyser)
    osc1.start()
    gain1Ref.current = g1

    // Fifth harmonic — 240Hz with different LFO rate (subtle beating)
    const osc2 = ctx.createOscillator()
    const g2 = ctx.createGain()
    osc2.type = 'sine'
    osc2.frequency.value = 240
    g2.gain.value = 0
    const lfo2 = ctx.createOscillator()
    const lfo2g = ctx.createGain()
    lfo2.type = 'sine'
    lfo2.frequency.value = 0.11
    lfo2g.gain.value = 2
    lfo2.connect(lfo2g)
    lfo2g.connect(osc2.frequency)
    lfo2.start()
    osc2.connect(g2)
    g2.connect(analyser)
    osc2.start()
    gain2Ref.current = g2
  }, [])

  // Toggle gain on/off
  useEffect(() => {
    if (!active) {
      // Fade out
      if (gain1Ref.current && ctxRef.current) {
        const t = ctxRef.current.currentTime
        gain1Ref.current.gain.linearRampToValueAtTime(0, t + 1)
        gain2Ref.current?.gain.linearRampToValueAtTime(0, t + 1)
      }
      return
    }

    // Initialize on first activation (needs user gesture)
    initAudio()

    // Resume if suspended
    if (ctxRef.current?.state === 'suspended') ctxRef.current.resume()

    // Fade in
    if (gain1Ref.current && ctxRef.current) {
      const t = ctxRef.current.currentTime
      gain1Ref.current.gain.linearRampToValueAtTime(0.025, t + 2)
      gain2Ref.current?.gain.linearRampToValueAtTime(0.015, t + 2)
    }
  }, [active, initAudio])

  // Duck when music player is playing
  const musicPlaying = useMusicStore(s => s.isPlaying)
  useEffect(() => {
    if (!active || !gain1Ref.current || !ctxRef.current) return
    const t = ctxRef.current.currentTime
    if (musicPlaying) {
      // Duck to near-silent
      gain1Ref.current.gain.linearRampToValueAtTime(0.005, t + 0.5)
      gain2Ref.current?.gain.linearRampToValueAtTime(0.003, t + 0.5)
    } else {
      // Restore
      gain1Ref.current.gain.linearRampToValueAtTime(0.025, t + 1)
      gain2Ref.current?.gain.linearRampToValueAtTime(0.015, t + 1)
    }
  }, [musicPlaying, active])

  // Waveform visualization
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    function draw() {
      rafRef.current = requestAnimationFrame(draw)
      const ctx = canvas!.getContext('2d')!
      const w = canvas!.width
      const h = canvas!.height
      ctx.clearRect(0, 0, w, h)

      const analyser = analyserRef.current
      if (!analyser || !useRoomTone.getState().active) {
        // Ambient sine wave when off — proof of life
        const t = Date.now() / 1000
        ctx.beginPath()
        ctx.strokeStyle = 'rgba(212, 160, 84, 0.12)'
        ctx.lineWidth = 1
        for (let j = 0; j < w; j++) {
          const y = h / 2 + Math.sin(j / 80 + t * 0.3) * 4 + Math.sin(j / 40 + t * 0.7) * 2
          if (j === 0) ctx.moveTo(0, y)
          else ctx.lineTo(j, y)
        }
        ctx.stroke()
        return
      }

      const data = new Uint8Array(analyser.frequencyBinCount)
      analyser.getByteTimeDomainData(data)
      ctx.beginPath()
      ctx.strokeStyle = 'rgba(212, 160, 84, 0.5)'
      ctx.lineWidth = 1.5
      const sliceW = w / data.length
      for (let i = 0; i < data.length; i++) {
        const v = data[i] / 128.0
        const y = v * h / 2
        if (i === 0) ctx.moveTo(0, y)
        else ctx.lineTo(i * sliceW, y)
      }
      ctx.stroke()
    }

    draw()
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  return (
    <canvas
      ref={canvasRef}
      width={typeof window !== 'undefined' ? window.innerWidth : 800}
      height={24}
      className="w-full"
      style={{
        position: 'fixed',
        bottom: 64, // above music player
        left: 0,
        right: 0,
        pointerEvents: 'none',
        zIndex: 5,
        opacity: 0.7,
      }}
    />
  )
}
